<template>
    <div class="relative space-y-6 lg:space-y-8 px-3 sm:px-4 lg:px-8 min-h-[calc(100vh-8rem)]">
        <!-- Header -->
        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
                <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary dark:text-secondary">
                Materials
                </h1>
                <p class="text-sm text-slate-500 dark:text-slate-200 mt-2">
                Manage all materials, documents, and online forms.
                </p>
            </div>
            <div class="flex items-center gap-2">
                <button @click="showStats = !showStats"
                    class="px-4 py-2 rounded-lg font-semibold transition border border-gray-300 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-gray-700 inline-flex items-center justify-center w-full sm:w-auto">
                    <Eye v-if="!showStats" class="h-5 w-5 mr-2" />
                    <EyeOff v-else class="h-5 w-5 mr-2" />
                    <span class="hidden sm:inline">{{ showStats ? 'Hide Stats' : 'Show Stats' }}</span>
                </button>
                <button @click="openAddModal"
                    class="px-6 py-2 rounded-lg font-semibold text-white transition bg-primary hover:bg-secondary
                        shadow-md hover:opacity-90 flex items-center justify-center w-full sm:w-auto">
                    <FolderPlus class="h-5 w-5 mr-2" />
                    <span class="hidden sm:inline">Add New Material</span>
                </button>
            </div>
        </div>

        <!-- STATS BAR (collapsible) -->
        <Transition name="stats">
            <div v-if="showStats" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
                <StatCard
                    label="Total Materials"
                    :value="stats.total"
                    :loading="loading"
                    :icon="FileText"
                    color="text-blue-600 dark:text-blue-400"
                />
                <StatCard
                    label="Active"
                    :value="stats.active"
                    :loading="loading"
                    :icon="CheckCircle"
                    color="text-emerald-600 dark:text-emerald-400"
                />
                <StatCard
                    label="Inactive"
                    :value="stats.inactive"
                    :loading="loading"
                    :icon="XCircle"
                    color="text-rose-600 dark:text-rose-400"
                />
            </div>
        </Transition>

        <!-- Search and Filters -->
        <TableControls
            v-model:search="searchQuery"
            v-model:status="statusFilter"
            v-model:perPage="perPage"
            :options="statusOptions"
            filterLabel="Status"
            placeholder="Search by title or description..."
            class="md:sticky md:top-[80px]"
        />

        <!-- Type Filter -->
        <div class="flex gap-2 -mt-2 mb-4 overflow-x-auto pb-2">
            <button v-for="type in typeOptions" :key="type.value"
                @click="typeFilter = type.value"
                class="px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap"
                :class="typeFilter === type.value ? 'bg-primary text-white shadow-sm' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'">
                {{ type.label }}
            </button>
        </div>

        <!-- Error State -->
        <div v-if="error" class="bg-red-50 text-red-600 p-4 rounded-xl border border-red-200">
        {{ error }}
        </div>

        <!-- Table -->
        <MaterialTable
            :materials="paginatedMaterials"
            :loading="loading"
            :totalUnfiltered="materials.length"
            @view="handleView"
            @download="handleDownload"
            @print="handlePrint"
            @edit="handleEdit"
            @editViewers="handleEditViewers"
            @toggleStatus="handleToggleStatus"
            @delete="handleDelete"
        />

        <!-- Pagination -->
        <div
            v-if="!loading && !error && filteredTotal > 0"
            class="card border border-gray-200/50 dark:border-gray-700/50 shadow-sm sticky bottom-0 z-30"
            >
            <div class="p-4">
                <PaginationBar
                :current-page="currentPage"
                :total-pages="filteredTotalPages"
                :total="filteredTotal"
                :per-page="perPage"
                @update:page="goToPage"
                />
            </div>
        </div>

        <MaterialDetailsModal
            :is-open="showDetailsModal"
            :material="selectedMaterial"
            @close="showDetailsModal = false; selectedMaterial = null"
        />

        <MaterialFormModal
            :is-open="showModal"
            :mode="modalMode"
            :form="currentForm"
            :error="modalError"
            :loading="modalLoading"
            @close="closeModal"
            @save="handleSave"
        />

        <!-- Viewers Edit Modal -->
        <ViewersEditModal
            :is-open="showViewersModal"
            :material-id="viewersEditId"
            :position-viewer-ids="viewersEditPositionIds"
            :user-viewer-ids="viewersEditUserIds"
            :loading="viewersModalLoading"
            @close="closeViewersModal"
            @save="handleSaveViewers"
        />

        <!-- Delete Confirmation Modal -->
        <Modal v-model="showDeleteConfirm" title="Delete Material">
            <p class="text-sm text-secondary-text/80 dark:text-secondary-dark-text/80">
                Are you sure you want to permanently delete
                <span class="font-semibold text-slate-700 dark:text-slate-300">"{{ pendingDeleteName }}"</span>?
                This action cannot be undone.
            </p>
            <template #footer>
                <button type="button"
                    class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors duration-200"
                    @click="cancelDelete">
                    Cancel
                </button>
                <button type="button"
                    class="inline-flex items-center ml-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-danger hover:bg-danger/90 shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                    :disabled="deleteLoading" @click="confirmDelete">
                    <Loader2 v-if="deleteLoading" class="w-4 h-4 mr-2 animate-spin" />
                    {{ deleteLoading ? 'Deleting...' : 'Yes, delete permanently' }}
                </button>
            </template>
        </Modal>

        <!-- Toggle Status Confirmation Modal -->
        <Modal v-model="showToggleConfirm" :title="toggleAction === 'deactivate' ? 'Deactivate Material' : 'Activate Material'">
            <p class="text-sm text-secondary-text/80 dark:text-secondary-dark-text/80">
                Are you sure you want to {{ toggleAction === 'deactivate' ? 'deactivate' : 'activate' }}
                <span class="font-semibold text-slate-700 dark:text-slate-300">"{{ pendingToggleName }}"</span>?
            </p>
            <template #footer>
                <button type="button"
                    class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors duration-200"
                    @click="cancelToggle">
                    Cancel
                </button>
                <button type="button"
                    class="flex justify-center ml-2 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                    :class="toggleAction === 'deactivate' ? 'bg-danger hover:bg-danger/90' : 'bg-success hover:bg-success/90'"
                    :disabled="toggleLoading" @click="confirmToggleStatus">
                    <Loader2 v-if="toggleLoading" class="w-4 h-4 mr-2 animate-spin" />
                    {{ toggleLoading ? 'Processing...' : `Yes, ${toggleAction === 'deactivate' ? 'deactivate' : 'activate'}` }}
                </button>
            </template>
        </Modal>

    </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { FolderPlus, Loader2, Eye, EyeOff, FileText, CheckCircle, XCircle } from 'lucide-vue-next'
import { materialService } from '@/services/material.service'
import api from '@/services/api'
import type { Material } from '@/config/types/material'
import MaterialTable from '@/components/materials/MaterialTable.vue'
import MaterialFormModal from '@/components/materials/MaterialFormModal.vue'
import MaterialDetailsModal from '@/components/materials/MaterialDetailsModal.vue'
import ViewersEditModal from '@/components/materials/ViewersEditModal.vue'
import Modal from '@/components/layout/Modal.vue'
import TableControls from '@/components/layout/TableControls.vue'
import PaginationBar from '@/components/layout/PaginationBar.vue'
import StatCard from '@/components/layout/StatCard.vue'
import { useToast } from 'vue-toastification'

const toast = useToast()
const toastTimeout = 5000
const router = useRouter()

const materials = ref<Material[]>([])
const loading = ref(false)
const error = ref('')

const showModal = ref(false)
const modalMode = ref<'add'|'edit'>('add')
const modalError = ref('')
const modalLoading = ref(false)
const currentForm = reactive({
    id: 0,
    title: '',
    description: '',
    type: 'Doc File' as 'Online form' | 'URL Link' | 'Doc File',
    source: null as File | string | null,
    url_link: '',
    status: 'active' as 'active' | 'inactive',
    position_viewers: [] as number[],
    user_viewers: [] as number[]
})

const showDeleteConfirm = ref(false)
const materialToDelete = ref<number | null>(null)
const deleteLoading = ref(false)

const pendingDeleteName = computed(() => {
    if (materialToDelete.value === null) return 'this material'
    const m = materials.value.find(item => item.id === materialToDelete.value)
    return m?.title || 'this material'
})

const cancelDelete = () => {
    showDeleteConfirm.value = false
    materialToDelete.value = null
}

const showToggleConfirm = ref(false)
const toggleLoading = ref(false)
const toggleAction = ref<'activate' | 'deactivate'>('activate')
const pendingToggleMaterial = ref<Material | null>(null)

const pendingToggleName = computed(() => pendingToggleMaterial.value?.title || 'this material')

const cancelToggle = () => {
    showToggleConfirm.value = false
    pendingToggleMaterial.value = null
}

const showDetailsModal = ref(false)
const selectedMaterial = ref<Material | null>(null)

const showViewersModal = ref(false)
const viewersEditId = ref(0)
const viewersEditPositionIds = ref<number[]>([])
const viewersEditUserIds = ref<number[]>([])
const viewersModalLoading = ref(false)

const showStats = ref(false)

// Stats
const stats = computed(() => {
    const list = materials.value || []
    return {
        total: list.length,
        active: list.filter(m => m.status === 'active').length,
        inactive: list.filter(m => m.status === 'inactive').length,
    }
})

// Filters (client-side)
const searchQuery = ref('')
const statusFilter = ref('')
const typeFilter = ref('')

const statusOptions = [
    { label: 'All', value: '' },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
]

const typeOptions = [
    { label: 'All Types', value: '' },
    { label: 'Document Files', value: 'Doc File' },
    { label: 'URL Links', value: 'URL Link' },
    { label: 'Online Forms', value: 'Online form' }
]

// Status filter
const statusFiltered = computed(() => {
    const list = materials.value || []
    if (!statusFilter.value) return list
    return list.filter(m => m.status === statusFilter.value)
})

// Type filter
const typeFiltered = computed(() => {
    const list = statusFiltered.value
    if (!typeFilter.value) return list
    return list.filter(m => m.type === typeFilter.value)
})

// Search filter
const searchFiltered = computed(() => {
    const list = typeFiltered.value
    if (!searchQuery.value.trim()) return list
    const query = searchQuery.value.toLowerCase()
    return list.filter(m => {
        const title = (m.title || '').toLowerCase()
        const description = (m.description || '').toLowerCase()
        return title.includes(query) || description.includes(query)
    })
})

// Pagination (client-side)
const perPage = ref(10)
const currentPage = ref(1)
const filteredTotalPages = computed(() => Math.max(1, Math.ceil(searchFiltered.value.length / perPage.value)))
const filteredTotal = computed(() => searchFiltered.value.length)

const paginatedMaterials = computed(() => {
    const start = (currentPage.value - 1) * perPage.value
    const end = start + perPage.value
    return searchFiltered.value.slice(start, end)
})

// Reset page when filters change
watch([statusFilter, searchQuery, typeFilter, perPage], () => {
    currentPage.value = 1
})

const goToPage = (page: number) => {
    currentPage.value = page
}

const fetchMaterials = async () => {
    loading.value = true
    error.value = ''
    try {
        const response = await materialService.fetchAll({ per_page: 1000 })
        materials.value = Array.isArray(response) ? response : (Array.isArray(response.data) ? response.data : [])
    } catch (err: any) {
        error.value = err.message || 'Failed to fetch materials'
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    fetchMaterials()
})

const openAddModal = () => {
    modalMode.value = 'add'
    modalError.value = ''
    currentForm.id = 0
    currentForm.title = ''
    currentForm.description = ''
    currentForm.type = 'Doc File'
    currentForm.source = null
    currentForm.url_link = ''
    currentForm.status = 'active'
    currentForm.position_viewers = []
    currentForm.user_viewers = []
    showModal.value = true
}

const handleEdit = (m: Material) => {
    modalMode.value = 'edit'
    modalError.value = ''
    currentForm.id = m.id
    currentForm.title = m.title
    currentForm.description = m.description || ''
    currentForm.type = m.type
    currentForm.source = null
    currentForm.url_link = m.url_link || ''
    currentForm.status = m.status
    currentForm.position_viewers = m.position_viewers?.map(v => v.id) || []
    currentForm.user_viewers = m.user_viewers?.map(v => v.id) || []
    showModal.value = true
}

const handleEditViewers = (m: Material) => {
    viewersEditId.value = m.id
    viewersEditPositionIds.value = m.position_viewers?.map(v => v.id) || []
    viewersEditUserIds.value = m.user_viewers?.map(v => v.id) || []
    showViewersModal.value = true
}

const closeViewersModal = () => {
    showViewersModal.value = false
    viewersModalLoading.value = false
}

const handleSaveViewers = async (data: { materialId: number; positionViewerIds: number[]; userViewerIds: number[] }) => {
    viewersModalLoading.value = true
    try {
        const m = materials.value.find(item => item.id === data.materialId)
        if (!m) throw new Error('Material not found')

        await api.post(`/materials/${data.materialId}`, {
            _method: 'PUT',
            title: m.title,
            type: m.type,
            status: m.status,
            description: m.description || null,
            url_link: m.url_link || null,
            position_viewers: data.positionViewerIds,
            user_viewers: data.userViewerIds,
        })
        toast.success('Viewers updated successfully', { timeout: toastTimeout })
        showViewersModal.value = false
        fetchMaterials()
    } catch (err: any) {
        toast.error(err.response?.data?.message || err.message || 'Failed to update viewers', { timeout: toastTimeout })
    } finally {
        viewersModalLoading.value = false
    }
}

const closeModal = () => {
    showModal.value = false
}

const handleSave = async (form: any) => {
    modalError.value = ''
    modalLoading.value = true
    try {
        const formData = new FormData()
        formData.append('title', form.title)
        if (form.description) formData.append('description', form.description)
        formData.append('type', form.type)
        formData.append('status', form.status)
        
        if (form.type === 'URL Link' || form.type === 'Online form') {
            formData.append('url_link', form.url_link)
        } else if (form.source instanceof File) {
            formData.append('file', form.source)
        }

        if (form.position_viewers.length > 0) {
            form.position_viewers.forEach((vId: number, index: number) => {
                formData.append(`position_viewers[${index}]`, vId.toString())
            })
        } else {
            formData.append('position_viewers', '')
        }
        if (form.user_viewers.length > 0) {
            form.user_viewers.forEach((vId: number, index: number) => {
                formData.append(`user_viewers[${index}]`, vId.toString())
            })
        } else {
            formData.append('user_viewers', '')
        }

        if (modalMode.value === 'add') {
            await materialService.create(formData)
            toast.success('Material uploaded successfully', { timeout: toastTimeout })
        } else {
            await materialService.update(currentForm.id, formData)
            toast.success('Material updated successfully', { timeout: toastTimeout })
        }
        closeModal()
        fetchMaterials()
    } catch (err: any) {
        modalError.value = err.response?.data?.message || err.message || 'An error occurred'
    } finally {
        modalLoading.value = false
    }
}

const handleDelete = (m: Material) => {
    materialToDelete.value = m.id
    showDeleteConfirm.value = true
}

const confirmDelete = async () => {
    if (!materialToDelete.value) return
    deleteLoading.value = true
    try {
        await materialService.delete(materialToDelete.value)
        toast.success('Material deleted successfully', { timeout: toastTimeout })
        showDeleteConfirm.value = false
        materialToDelete.value = null
        if (materials.value.length === 1 && currentPage.value > 1) {
            currentPage.value--
        }
        fetchMaterials()
    } catch (err: any) {
        toast.error(err.message || 'Failed to delete material', { timeout: toastTimeout })
    } finally {
        deleteLoading.value = false
    }
}

const handleToggleStatus = (m: Material) => {
    pendingToggleMaterial.value = m
    toggleAction.value = m.status === 'active' ? 'deactivate' : 'activate'
    showToggleConfirm.value = true
}

const confirmToggleStatus = async () => {
    const m = pendingToggleMaterial.value
    if (!m) return
    toggleLoading.value = true
    try {
        const formData = new FormData()
        formData.append('title', m.title)
        formData.append('type', m.type)
        formData.append('status', m.status === 'active' ? 'inactive' : 'active')
        if (m.description) formData.append('description', m.description)
        if (m.url_link) formData.append('url_link', m.url_link)
        
        m.position_viewers?.forEach((v, index) => formData.append(`position_viewers[${index}]`, v.id.toString()))
        m.user_viewers?.forEach((v, index) => formData.append(`user_viewers[${index}]`, v.id.toString()))

        await materialService.update(m.id, formData)
        toast.success('Status updated successfully', { timeout: toastTimeout })
        showToggleConfirm.value = false
        pendingToggleMaterial.value = null
        fetchMaterials()
    } catch (err: any) {
        toast.error(err.message || 'Failed to update status', { timeout: toastTimeout })
    } finally {
        toggleLoading.value = false
    }
}

const handleView = (m: Material) => {
  if (m.type === 'Online form') {
    router.push({ name: 'view-online-form', params: { id: m.id } })
  } else {
    selectedMaterial.value = m
    showDetailsModal.value = true
  }
}

const handleDownload = async (m: Material) => {
    try {
        const response = await fetch(materialService.getDownloadUrl(m.id), {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        })
        if (!response.ok) throw new Error('Invalid File')
        
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const disposition = response.headers.get('content-disposition')
        let filename = m.title
        if (disposition && disposition.indexOf('attachment') !== -1) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
            const matches = filenameRegex.exec(disposition)
            if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, '')
            }
        } else if (m.file_path) {
            const parts = m.file_path.split('.')
            if (parts.length > 1) {
                filename = `${m.title}.${parts[parts.length - 1]}`
            }
        }
        
        a.download = filename
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
    } catch (err: any) {
        toast.error(err.message || 'Failed to download file', { timeout: toastTimeout })
    }
}

const handlePrint = async (m: Material) => {
    if (!m.file_path) return
    const loadingToast = toast.info('Preparing document for printing...', { timeout: false })
    try {
        await materialService.printFile(m.id)
        toast.dismiss(loadingToast)
        toast.success('Document printed successfully', { timeout: toastTimeout })
    } catch (err: any) {
        toast.dismiss(loadingToast)
        toast.error(err.message || 'Failed to print file', { timeout: toastTimeout })
    }
}
</script>

<style scoped>
.stats-enter-active,
.stats-leave-active {
    transition: all 0.3s ease;
}
.stats-enter-from,
.stats-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}
</style>
